/**
 * Field visibility utility for evaluating visible_if conditions.
 *
 * Fields (and individual SelectOptions) in the connector schema can have a
 * `visible_if` property — a map of form-field names to expected values.
 * ALL conditions must match (AND logic) for the field/option to be visible.
 *
 * Matching rules for each (actual, expected) pair:
 *   - both arrays      -> any overlap passes
 *   - actual array     -> expected must be in actual  (multi-select contains)
 *   - expected array   -> actual must be in expected  (one-of membership)
 *   - both scalars     -> strict equality
 *
 * Example:
 *   visible_if: { report_view: ["performance_agg", "geo_agg"] }
 *   -> visible only when formValues.report_view === "performance_agg" OR "geo_agg"
 */

import type {
  ConnectorFieldSchema,
  SelectOption,
} from '../../types/connector-schema';

function matchesCondition(actual: unknown, expected: unknown): boolean {
  const actualIsArray = Array.isArray(actual);
  const expectedIsArray = Array.isArray(expected);

  if (actualIsArray && expectedIsArray) {
    return (actual as unknown[]).some((a) => (expected as unknown[]).includes(a));
  }
  if (actualIsArray) {
    return (actual as unknown[]).includes(expected);
  }
  if (expectedIsArray) {
    // Treat undefined/null/"" as no-match when expected is a list.
    if (actual === undefined || actual === null || actual === '') return false;
    return (expected as unknown[]).includes(actual);
  }
  return actual === expected;
}

function evaluate(
  conditions: Record<string, unknown> | undefined,
  formValues: Record<string, unknown>,
): boolean {
  if (!conditions || Object.keys(conditions).length === 0) return true;
  return Object.entries(conditions).every(([key, expected]) =>
    matchesCondition(formValues[key], expected),
  );
}

/** Check whether a field is visible under the current form values. */
export function isFieldVisible(
  field: ConnectorFieldSchema,
  formValues: Record<string, unknown>,
): boolean {
  // `hidden` is unconditional (independent of form values / visible_if): a
  // hidden field is never rendered, but its default is still seeded into form
  // state by the consuming form, so the value persists. (#484)
  if (field.hidden) return false;
  return evaluate(field.visible_if, formValues);
}

/** Check whether a single SelectOption (inside a field) is visible. */
export function isOptionVisible(
  option: SelectOption,
  formValues: Record<string, unknown>,
): boolean {
  return evaluate(option.visible_if, formValues);
}

/** Get the set of visible field names for the current form state. */
export function getVisibleFieldNames(
  fields: ConnectorFieldSchema[],
  formValues: Record<string, unknown>,
): Set<string> {
  return new Set(
    fields
      .filter((field) => isFieldVisible(field, formValues))
      .map((field) => field.name),
  );
}

/**
 * Generate a stable key from visibility state for memoization.
 * When this key changes, Zod schemas need to be rebuilt.
 */
export function getVisibilityKey(
  fields: ConnectorFieldSchema[],
  formValues: Record<string, unknown>,
): string {
  const visibleNames = fields
    .filter((field) => field.visible_if)
    .map((field) => `${field.name}:${isFieldVisible(field, formValues) ? '1' : '0'}`)
    .join(',');
  return visibleNames || 'all-visible';
}
